/**
 * Phase D — OnboardingAttachBanner (Web)
 *
 * Compact banner shown at the top of the Stage tab while the backend's
 * signup-attach worker is still creating the user's teams in global
 * leagues. Polls /teams/my?envelope=v2 via the useOnboardingAttachStatus
 * hook and auto-hides on completion.
 *
 * Separate from OnboardingBanner.js (roster-completion checklist for
 * users whose teams ARE attached but who haven't drafted yet).
 */
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { motion, AnimatePresence } from 'framer-motion';

export default function OnboardingAttachBanner({ attached, total, status, onRetry }) {
  // Status branches:
  //   'in_progress' — show progress bar + caption
  //   'timeout'     — show retry CTA
  //   'checking'    — brief spinner caption while the first poll lands
  //   'complete' / 'idle' — render nothing (auto-hide)
  if (status === 'complete' || status === 'idle') return null;

  const ratio = total > 0 ? Math.min(1, attached / total) : 0;
  const percentage = Math.round(ratio * 100);
  const isTimeout = status === 'timeout';
  const caption = isTimeout
    ? 'Still setting up — click retry below'
    : `Setting up your teams (${attached} of ${total || '\u2026'})`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="mx-4 mb-3 bg-card border border-border/60 rounded-xl p-3 backdrop-blur-sm"
        data-testid="onboarding-attach-banner"
      >
        <div className="flex items-center gap-2">
          {isTimeout ? (
            <AlertCircle className="h-4 w-4 text-amber-400 shrink-0" />
          ) : (
            <RefreshCw className="h-4 w-4 text-accent animate-spin shrink-0" />
          )}
          <span
            className="flex-1 text-sm font-medium text-foreground truncate"
            data-testid="onboarding-attach-caption"
          >
            {caption}
          </span>
          {isTimeout && (
            <Button
              size="sm"
              variant="default"
              className="h-7 px-3 text-xs font-semibold"
              onClick={onRetry}
              data-testid="onboarding-attach-retry-button"
            >
              Retry
            </Button>
          )}
        </div>
        {!isTimeout && (
          <div className="mt-2">
            <Progress
              value={percentage}
              className="h-1"
              data-testid="onboarding-attach-progress-fill"
            />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
